import React, { useState, useCallback } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  useTheme,
  alpha
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  ShowChart as ShowChartIcon
} from '@mui/icons-material';

const SalesData = ({ companyId }) => {
  const theme = useTheme();
  const [salesData, setSalesData] = useState({
    monthlySales: [],
    topOrders: [],
    topCountries: [],
    topProducts: [],
    salesMetrics: {
      totalRevenue: 0,
      averageOrderValue: 0,
      totalCustomers: 0,
      totalOrders: 0,
      monthlyGrowth: 0
    }
  });

  const fetchSalesData = useCallback(async () => {
    try {
      // Fetch basic sales metrics from invoices table
      const { data: invoicesData, error: invoicesError } = await supabase
        .from('invoices')
        .select('total_amount, amount_due, invoice_date, customer_id, shipping_country, billing_country')
        .eq('company_id', companyId)
        .not('total_amount', 'is', null);

      if (invoicesError) throw invoicesError;

      // Fetch customer count
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('id, country, total_spent')
        .eq('company_id', companyId);

      if (customersError) throw customersError;

      // Fetch top orders (highest value invoices)
      const { data: topOrdersData, error: ordersError } = await supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          total_amount,
          amount_due,
          invoice_date,
          customers:customer_id (
            name,
            company
          )
        `)
        .eq('company_id', companyId)
        .not('total_amount', 'is', null)
        .order('total_amount', { ascending: false })
        .limit(5);

      if (ordersError) throw ordersError;

      // Fetch top products from invoice items
      const { data: topProductsData, error: productsError } = await supabase
        .from('invoice_items')
        .select(`
          description,
          quantity,
          total_price,
          invoices!inner(company_id),
          products(name)
        `)
        .eq('invoices.company_id', companyId)
        .not('total_price', 'is', null);

      if (productsError) throw productsError;

      // Process data
      const totalRevenue = invoicesData?.reduce((sum, invoice) => sum + (parseFloat(invoice.amount_due || invoice.total_amount) || 0), 0) || 0;
      const totalOrders = invoicesData?.length || 0;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
      const totalCustomers = customersData?.length || 0;

      // Calculate monthly sales data
      const monthlyData = {};
      invoicesData?.forEach(invoice => {
        const date = new Date(invoice.invoice_date);
        const monthKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
        
        if (!monthlyData[monthKey]) {
          monthlyData[monthKey] = { revenue: 0, orders: 0 };
        }
        
        monthlyData[monthKey].revenue += parseFloat(invoice.amount_due || invoice.total_amount) || 0;
        monthlyData[monthKey].orders += 1;
      });

      const monthlySales = Object.entries(monthlyData)
        .map(([month, data]) => ({ month, ...data }))
        .sort((a, b) => new Date(a.month) - new Date(b.month))
        .slice(-6); // Last 6 months

      // Calculate top countries from invoice shipping addresses
      const countryData = {};
      invoicesData?.forEach(invoice => {
        // Use shipping_country first, fallback to billing_country, then 'Unknown'
        const country = invoice.shipping_country || invoice.billing_country || 'Unknown';
        if (!countryData[country]) {
          countryData[country] = { orders: 0, revenue: 0 };
        }
        countryData[country].orders += 1;
        countryData[country].revenue += parseFloat(invoice.amount_due || invoice.total_amount) || 0;
      });

      const topCountries = Object.entries(countryData)
        .map(([country, data]) => ({ country, orders: data.orders, revenue: data.revenue }))
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 5);

      // Calculate top products
      const productData = {};
      topProductsData?.forEach(item => {
        const productName = item.products?.name || item.description || 'Unknown Product';
        if (!productData[productName]) {
          productData[productName] = { total_revenue: 0, total_quantity: 0 };
        }
        productData[productName].total_revenue += parseFloat(item.total_price) || 0;
        productData[productName].total_quantity += parseFloat(item.quantity) || 0;
      });

      const topProducts = Object.entries(productData)
        .map(([product_name, data]) => ({ product_name, ...data }))
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 5);

      // Calculate monthly growth (comparing last 2 months)
      let monthlyGrowth = 0;
      if (monthlySales.length >= 2) {
        const currentMonth = monthlySales[monthlySales.length - 1].revenue;
        const previousMonth = monthlySales[monthlySales.length - 2].revenue;
        monthlyGrowth = previousMonth > 0 ? ((currentMonth - previousMonth) / previousMonth) * 100 : 0;
      }

      setSalesData({
        monthlySales,
        topOrders: topOrdersData || [],
        topCountries,
        topProducts,
        salesMetrics: {
          totalRevenue,
          averageOrderValue,
          totalCustomers,
          totalOrders,
          monthlyGrowth
        }
      });

    } catch (err) {
      console.error('Error fetching sales data:', err);
      // Initialize empty data on error
      setSalesData({
        monthlySales: [],
        topOrders: [],
        topCountries: [],
        topProducts: [],
        salesMetrics: {
          totalRevenue: 0,
          averageOrderValue: 0,
          totalCustomers: 0,
          totalOrders: 0,
          monthlyGrowth: 0
        }
      });
    }
  }, [companyId]);

  React.useEffect(() => {
    if (companyId) {
      fetchSalesData();
    }
  }, [companyId, fetchSalesData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{ mb: 6 }}>
      {/* Sales Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.success.main, 0.1),
                  mr: 2
                }}>
                  <AttachMoneyIcon sx={{ fontSize: 24, color: 'success.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Total Revenue
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'success.main',
                mb: 1
              }}>
                {formatCurrency(salesData.salesMetrics.totalRevenue)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {salesData.salesMetrics.monthlyGrowth >= 0 ? (
                  <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                ) : (
                  <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                )}
                <Typography variant="body2" sx={{ 
                  color: salesData.salesMetrics.monthlyGrowth >= 0 ? 'success.main' : 'error.main',
                  fontWeight: 500
                }}>
                  {Math.abs(salesData.salesMetrics.monthlyGrowth).toFixed(1)}% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  mr: 2
                }}>
                  <ShowChartIcon sx={{ fontSize: 24, color: 'info.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Average Order Value
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'info.main',
                mb: 1
              }}>
                {formatCurrency(salesData.salesMetrics.averageOrderValue)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Per order
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.warning.main, 0.1),
                  mr: 2
                }}>
                  <ReceiptIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Total Orders
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'warning.main',
                mb: 1
              }}>
                {formatNumber(salesData.salesMetrics.totalOrders)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Invoices processed
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  mr: 2
                }}>
                  <AccountBalanceIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                  Total Customers
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 500, 
                color: 'primary.main',
                mb: 1
              }}>
                {formatNumber(salesData.salesMetrics.totalCustomers)}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Active customers
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sales Widgets Grid */}
      <Grid container spacing={4}>
        {/* Monthly Sales Chart */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Monthly Sales Trend
              </Typography>
              {salesData.monthlySales.length > 0 ? (
                <Box sx={{ 
                  height: 280, 
                  width: '100%',
                  bgcolor: 'white',
                  borderRadius: 2,
                  p: 2,
                  border: `1px solid ${theme.palette.grey[200]}`
                }}>
                  {/* Y-Axis and Chart Container */}
                  <Box sx={{ 
                    display: 'flex', 
                    height: '100%',
                    gap: 2
                  }}>
                    {/* Y-Axis Labels */}
                    <Box sx={{ 
                      width: 60, 
                      display: 'flex', 
                      flexDirection: 'column-reverse',
                      justifyContent: 'space-between',
                      py: 2,
                      pr: 1
                    }}>
                      {Array.from({ length: 5 }, (_, i) => {
                        const maxRevenue = Math.max(...salesData.monthlySales.map(m => m.revenue), 100);
                        const value = (maxRevenue / 4) * i;
                        return (
                          <Typography 
                            key={i} 
                            variant="caption" 
                            sx={{ 
                              color: 'text.secondary',
                              fontSize: '0.75rem',
                              textAlign: 'right',
                              lineHeight: 1
                            }}
                          >
                            ${value >= 1000 ? `${(value/1000).toFixed(0)}K` : Math.round(value)}
                          </Typography>
                        );
                      })}
                    </Box>
                    
                    {/* Chart Area */}
                    <Box sx={{ 
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%'
                    }}>
                      {/* Bars Container */}
                      <Box sx={{ 
                        flex: 1,
                        display: 'flex',
                        alignItems: 'end',
                        gap: 3,
                        px: 2,
                        pb: 1,
                        position: 'relative',
                        borderLeft: `2px solid ${theme.palette.grey[200]}`,
                        borderBottom: `2px solid ${theme.palette.grey[200]}`
                      }}>
                        {/* Grid Lines */}
                        {Array.from({ length: 4 }, (_, i) => (
                          <Box
                            key={i}
                            sx={{
                              position: 'absolute',
                              left: 0,
                              right: 0,
                              bottom: `${(i + 1) * 25}%`,
                              height: '1px',
                              bgcolor: alpha(theme.palette.grey[300], 0.5),
                              zIndex: 0
                            }}
                          />
                        ))}
                        
                        {/* Bars */}
                        {salesData.monthlySales.map((monthData, index) => {
                          const maxRevenue = Math.max(...salesData.monthlySales.map(m => m.revenue), 100);
                          const barHeight = Math.max((monthData.revenue / maxRevenue) * 100, 8);
                          
                          return (
                            <Box 
                              key={index} 
                              sx={{ 
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                position: 'relative',
                                zIndex: 1
                              }}
                            >
                              {/* Tooltip */}
                              <Box
                                sx={{
                                  position: 'absolute',
                                  bottom: `${barHeight + 5}%`,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  bgcolor: 'rgba(0,0,0,0.9)',
                                  color: 'white',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: '0.75rem',
                                  whiteSpace: 'nowrap',
                                  opacity: 0,
                                  pointerEvents: 'none',
                                  transition: 'opacity 0.2s ease',
                                  zIndex: 100,
                                  '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    borderLeft: '4px solid transparent',
                                    borderRight: '4px solid transparent',
                                    borderTop: '4px solid rgba(0,0,0,0.9)'
                                  }
                                }}
                                className="chart-tooltip"
                              >
                                {formatCurrency(monthData.revenue)}
                              </Box>
                              
                              {/* Bar */}
                              <Box
                                sx={{
                                  width: 32,
                                  height: `${barHeight}%`,
                                  minHeight: 190,
                                  bgcolor: '#4caf50',
                                  borderRadius: '3px 3px 0 0',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                  '&:hover': {
                                    bgcolor: '#66bb6a',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                    '& ~ .chart-tooltip': {
                                      opacity: 1
                                    }
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  const tooltip = e.currentTarget.parentElement.querySelector('.chart-tooltip');
                                  if (tooltip) tooltip.style.opacity = '1';
                                }}
                                onMouseLeave={(e) => {
                                  const tooltip = e.currentTarget.parentElement.querySelector('.chart-tooltip');
                                  if (tooltip) tooltip.style.opacity = '0';
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                      
                      {/* X-Axis Labels */}
                      <Box sx={{ 
                        display: 'flex',
                        gap: 3,
                        px: 2,
                        pt: 1.5,
                        ml: 2
                      }}>
                        {salesData.monthlySales.map((monthData, index) => (
                          <Box key={index} sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 500,
                                fontSize: '0.8rem'
                              }}
                            >
                              {monthData.month}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ 
                  height: 300, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="h6">No sales data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Orders */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Top Orders
              </Typography>
              {salesData.topOrders.length > 0 ? (
                <Stack spacing={2}>
                  {salesData.topOrders.map((order, index) => (
                    <Box key={order.id} sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      bgcolor: alpha(theme.palette.grey[100], 0.5),
                      border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      }
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                          {order.invoice_number}
                        </Typography>
                        <Chip 
                          label={`#${index + 1}`}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            fontWeight: 500
                          }}
                        />
                      </Box>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        {order.customers?.name || order.customers?.company || 'Unknown Customer'}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                          {formatCurrency(order.amount_due || order.total_amount)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {formatDate(order.invoice_date)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No orders found</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Countries */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Top Countries
              </Typography>
              {salesData.topCountries.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Country</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Orders</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salesData.topCountries.map((country, index) => (
                        <TableRow key={country.country} sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Chip 
                                label={index + 1}
                                size="small"
                                sx={{ 
                                  mr: 1,
                                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                                  color: 'primary.main',
                                  fontWeight: 500,
                                  minWidth: 24
                                }}
                              />
                              {country.country}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {formatNumber(country.orders)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, color: 'success.main' }}>
                            {formatCurrency(country.revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No customer data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Top Products */}
        <Grid item xs={12} md={6}>
          <Card sx={{ 
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.18)}`
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 500, 
                color: 'text.primary',
                mb: 3
              }}>
                Top Products
              </Typography>
              {salesData.topProducts.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 500, color: 'text.primary' }}>Product</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Qty</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 500, color: 'text.primary' }}>Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {salesData.topProducts.map((product, index) => (
                        <TableRow key={product.product_name} sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05)
                          }
                        }}>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Chip 
                                label={index + 1}
                                size="small"
                                sx={{ 
                                  mr: 1,
                                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                  color: 'secondary.main',
                                  fontWeight: 500,
                                  minWidth: 24
                                }}
                              />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {product.product_name}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500 }}>
                            {formatNumber(product.total_quantity)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 500, color: 'success.main' }}>
                            {formatCurrency(product.total_revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ 
                  height: 200, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  color: 'text.secondary'
                }}>
                  <Typography variant="body1">No product data available</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesData;